import React, { useEffect, useState } from 'react';
import { doc, onSnapshot, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db, auth } from '@/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import {
  Copy,
  Check,
  Share2,
  Users,
  MousePointerClick,
  TrendingUp,
  DollarSign,
  ArrowRight,
  ShieldOff,
  Gift,
  Star,
  Clock,
  X,
  Send,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface InfluencerStats {
  clicks: number;
  signups: number;
  freeToBasic: number;
  freeToPremium: number;
  trialToSubscriber: number;
  totalRevenue: number;
  activeReferrals: number;
}

interface InfluencerDoc {
  uid: string;
  displayName: string;
  creatorCode: string;
  status: 'active' | 'paused' | 'suspended';
  tier: 'standard' | 'premium';
  payoutRate: number;
  stats: InfluencerStats;
}

const BASE_URL = 'https://everydaytheapp.com/ref';

// ─── Stat card ────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: number | string;
  icon: React.FC<{ className?: string }>;
  format?: 'number' | 'currency' | 'percent';
  highlight?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  icon: Icon,
  format = 'number',
  highlight = false,
}) => {
  const display =
    format === 'currency'
      ? `$${Number(value).toFixed(2)}`
      : format === 'percent'
      ? `${value}%`
      : value;

  return (
    <div
      className={`flex flex-col gap-2 p-5 rounded-2xl border ${
        highlight
          ? 'bg-primary-500/10 border-primary-500/30'
          : 'bg-slate-900 border-slate-800'
      }`}
    >
      <Icon className={`w-5 h-5 ${highlight ? 'text-primary-400' : 'text-slate-500'}`} />
      <p className="text-2xl font-black text-white tabular-nums">{display}</p>
      <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">{label}</p>
    </div>
  );
};

// ─── Funnel bar ───────────────────────────────────────────────────────────────

const FunnelBar: React.FC<{ label: string; value: number; max: number; color: string }> = ({
  label,
  value,
  max,
  color,
}) => {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between text-xs">
        <span className="text-slate-400">{label}</span>
        <span className="font-mono text-slate-300">
          {value} <span className="text-slate-600">({pct}%)</span>
        </span>
      </div>
      <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

// ─── Refer-a-Friend view (non-influencer users) ───────────────────────────────

const ReferFriendView: React.FC<{ uid: string }> = ({ uid }) => {
  const [copied, setCopied] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [referralUrl, setReferralUrl] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [loadingCode, setLoadingCode] = useState(true);

  // Creator application state
  const [appStatus, setAppStatus] = useState<null | 'pending' | 'approved' | 'rejected'>(null);
  const [loadingApp, setLoadingApp] = useState(true);
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formSocial, setFormSocial] = useState('');
  const [formAudience, setFormAudience] = useState('');
  const [formReason, setFormReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    const fetchCode = async () => {
      try {
        const fns = getFunctions(getApp());
        const getCode = httpsCallable<Record<string, string>, { code: string }>(fns, 'apiRewards');
        const result = await getCode({ action: 'getPersonalReferralCode' });
        setReferralCode(result.data.code);
        setReferralUrl(`${BASE_URL}/${result.data.code}`);
      } catch {
        setReferralUrl('https://everydaytheapp.com');
      } finally {
        setLoadingCode(false);
      }
    };
    if (uid) fetchCode();
  }, [uid]);

  useEffect(() => {
    const checkApp = async () => {
      if (!uid) { setLoadingApp(false); return; }
      try {
        const snap = await getDoc(doc(db, 'creatorApplications', uid));
        if (snap.exists()) {
          setAppStatus((snap.data() as { status: string }).status as 'pending' | 'approved' | 'rejected');
        }
      } catch {}
      setLoadingApp(false);
    };
    checkApp();
  }, [uid]);

  const handleCopy = async () => {
    if (!referralUrl) return;
    await navigator.clipboard.writeText(referralUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleCopyCode = async () => {
    if (!referralCode) return;
    await navigator.clipboard.writeText(referralCode);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2500);
  };

  const handleShare = async () => {
    if (!navigator.share) { handleCopy(); return; }
    await navigator.share({
      title: 'EverydayOS — The household operating system',
      text: 'I use EverydayOS to manage my household — tasks, groceries, calendar, and more. Check it out:',
      url: referralUrl,
    }).catch(() => {});
  };

  const handleApplySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim() || !formEmail.trim() || !formReason.trim()) {
      setSubmitError('Name, email, and reason are required.');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      await setDoc(doc(db, 'creatorApplications', uid), {
        uid,
        displayName: formName.trim(),
        email: formEmail.trim(),
        socialLinks: formSocial.trim(),
        audienceSize: formAudience.trim(),
        reason: formReason.trim(),
        submittedAt: Timestamp.now(),
        status: 'pending',
      });
      setAppStatus('pending');
      setShowApplyForm(false);
    } catch (err: any) {
      setSubmitError(err?.message ?? 'Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const appStatusConfig = {
    pending: {
      icon: Clock,
      label: 'Application Under Review',
      description: 'We\'ve received your application and will review it shortly. You\'ll be notified when a decision is made.',
      cls: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
    },
    approved: {
      icon: Check,
      label: 'Application Approved!',
      description: 'Congratulations! Your creator account is being set up. Refresh the page to access your Creator Dashboard.',
      cls: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
    },
    rejected: {
      icon: X,
      label: 'Application Not Approved',
      description: 'Unfortunately your application wasn\'t approved at this time. You can contact support if you have questions.',
      cls: 'text-red-400 bg-red-500/10 border-red-500/30',
    },
  };

  return (
    <div className="max-w-xl mx-auto p-6 space-y-8">
      {/* Apply form modal */}
      {showApplyForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md p-6 space-y-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-black text-white">Apply to Creator Program</h2>
              <button onClick={() => setShowApplyForm(false)} className="text-slate-500 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-400">
              Tell us about yourself. We review all applications within 3–5 business days.
            </p>
            {submitError && (
              <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl p-3">
                {submitError}
              </div>
            )}
            <form onSubmit={handleApplySubmit} className="space-y-4">
              {[
                { label: 'Your Name *', value: formName, set: setFormName, placeholder: 'Jane Smith', type: 'text' },
                { label: 'Email Address *', value: formEmail, set: setFormEmail, placeholder: 'jane@example.com', type: 'email' },
                { label: 'Social Links', value: formSocial, set: setFormSocial, placeholder: 'Instagram, YouTube, TikTok URLs…', type: 'text' },
                { label: 'Audience Size', value: formAudience, set: setFormAudience, placeholder: 'e.g. 10k followers, 50k subscribers', type: 'text' },
              ].map(({ label, value, set, placeholder, type }) => (
                <div key={label} className="space-y-1">
                  <label className="text-xs text-slate-400 font-semibold uppercase tracking-wider">{label}</label>
                  <input
                    type={type}
                    value={value}
                    onChange={e => set(e.target.value)}
                    placeholder={placeholder}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-primary-500"
                  />
                </div>
              ))}
              <div className="space-y-1">
                <label className="text-xs text-slate-400 font-semibold uppercase tracking-wider">Why do you want to join? *</label>
                <textarea
                  value={formReason}
                  onChange={e => setFormReason(e.target.value)}
                  placeholder="Tell us about your content, audience, and why EverydayOS would be a good fit…"
                  rows={4}
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-primary-500 resize-none"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold rounded-xl py-2.5 text-sm transition-colors flex items-center justify-center gap-2"
              >
                {submitting ? 'Submitting…' : (
                  <>
                    <Send className="w-4 h-4" />
                    Submit Application
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      <div>
        <p className="text-xs text-slate-500 uppercase tracking-widest font-mono mb-1">Referrals</p>
        <h1 className="text-2xl font-black text-white tracking-tight">Refer a Friend</h1>
        <p className="text-slate-400 text-sm mt-2">
          Know someone who'd love EverydayOS? Share your link or code — the more households, the better.
        </p>
      </div>

      <div className="bg-gradient-to-br from-primary-500/15 to-primary-600/5 border border-primary-500/30 rounded-2xl p-6 space-y-4">
        {/* Referral code copy */}
        {referralCode && (
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[10px] text-primary-400 uppercase tracking-widest font-semibold mb-0.5">Your Code</p>
              <p className="text-2xl font-black text-white tracking-widest font-mono">{referralCode}</p>
            </div>
            <button
              onClick={handleCopyCode}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-xs font-semibold transition-colors shrink-0"
            >
              {copiedCode ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              {copiedCode ? 'Copied!' : 'Copy Code'}
            </button>
          </div>
        )}
        {/* Referral URL copy */}
        <div className="flex items-center gap-2 p-3 bg-slate-900/60 rounded-xl">
          <span className="text-sm text-slate-400 font-mono truncate flex-1">
            {loadingCode ? 'Generating your link…' : referralUrl}
          </span>
          <button
            onClick={handleCopy}
            disabled={loadingCode}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-lg text-xs font-semibold transition-colors shrink-0"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
        </div>
        <button
          onClick={handleShare}
          className="flex items-center gap-2 text-primary-400 hover:text-primary-300 text-sm font-medium transition-colors"
        >
          <Share2 className="w-4 h-4" />
          Share with friends
        </button>
      </div>

      {/* Creator program section */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Star className="w-5 h-5 text-amber-400" />
          <p className="text-sm font-bold text-white">Become a Creator</p>
        </div>
        <p className="text-sm text-slate-400">
          Content creators and community builders can apply for the EverydayOS Creator Program — earn a referral commission on every paid signup from your audience.
        </p>

        {loadingApp ? null : appStatus ? (
          (() => {
            const cfg = appStatusConfig[appStatus];
            const Icon = cfg.icon;
            return (
              <div className={`flex items-start gap-3 p-4 rounded-xl border ${cfg.cls}`}>
                <Icon className="w-5 h-5 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold">{cfg.label}</p>
                  <p className="text-xs mt-0.5 opacity-80">{cfg.description}</p>
                </div>
              </div>
            );
          })()
        ) : (
          <button
            onClick={() => setShowApplyForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-400 text-sm font-semibold rounded-xl transition-colors"
          >
            <Gift className="w-4 h-4" />
            Apply to the Creator Program
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export const InfluencerDashboard: React.FC = () => {
  const [influencer, setInfluencer] = useState<InfluencerDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [notEnrolled, setNotEnrolled] = useState(false);
  const [suspended, setSuspended] = useState(false);

  const uid = auth.currentUser?.uid;

  useEffect(() => {
    if (!uid) {
      setNotEnrolled(true);
      setLoading(false);
      return;
    }

    const ref = doc(db, 'influencers', uid);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) {
        setNotEnrolled(true);
      } else {
        const data = snap.data() as InfluencerDoc;
        if (data.status === 'suspended') {
          setSuspended(true);
        } else {
          setInfluencer(data);
        }
      }
      setLoading(false);
    });

    return unsub;
  }, [uid]);

  const referralLink = influencer ? `${BASE_URL}/${influencer.creatorCode}` : '';

  const handleCopy = async () => {
    if (!referralLink) return;
    await navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleShare = async () => {
    if (!referralLink || !navigator.share) {
      handleCopy();
      return;
    }
    await navigator.share({
      title: 'Join me on EverydayOS',
      text: 'Manage your household smarter. Use my link to get started:',
      url: referralLink,
    }).catch(() => {}); // user cancelled — ignore
  };

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="p-8 animate-pulse space-y-4 max-w-3xl">
        <div className="h-28 bg-slate-800 rounded-2xl" />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[...Array(5)].map((_, i) => <div key={i} className="h-24 bg-slate-800 rounded-2xl" />)}
        </div>
      </div>
    );
  }

  // ── Not enrolled — show refer-a-friend page for regular users ───────────────
  if (notEnrolled) {
    return <ReferFriendView uid={uid ?? ''} />;
  }

  // ── Suspended creator ────────────────────────────────────────────────────────
  if (suspended || !influencer) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-center p-8">
        <ShieldOff className="w-12 h-12 text-slate-600" />
        <h2 className="text-xl font-bold text-white">Creator Access Suspended</h2>
        <p className="text-slate-500 text-sm max-w-sm">
          Your creator account has been suspended. Contact{' '}
          <a href="mailto:support@everydaytheapp.com" className="text-primary-400 hover:underline">
            support
          </a>{' '}
          if you believe this is an error.
        </p>
      </div>
    );
  }

  const { stats, creatorCode, tier, payoutRate } = influencer;
  const totalConversions =
    stats.freeToBasic + stats.freeToPremium + stats.trialToSubscriber;
  const conversionRate =
    stats.signups > 0 ? Math.round((totalConversions / stats.signups) * 100) : 0;

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div>
        <p className="text-xs text-slate-500 uppercase tracking-widest font-mono mb-1">
          Creator Dashboard
        </p>
        <h1 className="text-2xl font-black text-white tracking-tight">
          {influencer.displayName}
        </h1>
        <div className="flex items-center gap-2 mt-1">
          <span className={`text-2xs font-black uppercase px-2 py-0.5 rounded-full ${
            tier === 'premium' ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-700 text-slate-400'
          }`}>
            {tier}
          </span>
          <span className="text-xs text-slate-500">{Math.round(payoutRate * 100)}% payout</span>
        </div>
      </div>

      {/* Creator Code Card */}
      <div className="bg-gradient-to-br from-primary-500/15 to-primary-600/5 border border-primary-500/30 rounded-2xl p-6 space-y-4">
        <p className="text-xs font-semibold text-primary-400 uppercase tracking-widest">
          Your Creator Code
        </p>
        <p className="text-5xl font-black text-white tracking-widest font-mono">
          {creatorCode}
        </p>
        <div className="flex items-center gap-2 p-3 bg-slate-900/60 rounded-xl">
          <span className="text-sm text-slate-400 font-mono truncate flex-1">{referralLink}</span>
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-xs font-semibold transition-colors shrink-0"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
        </div>
        <button
          onClick={handleShare}
          className="flex items-center gap-2 text-primary-400 hover:text-primary-300 text-sm font-medium transition-colors"
        >
          <Share2 className="w-4 h-4" />
          Share with your audience
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatCard label="Link Clicks" value={stats.clicks} icon={MousePointerClick} />
        <StatCard label="Sign-ups" value={stats.signups} icon={Users} />
        <StatCard label="Conversions" value={totalConversions} icon={TrendingUp} highlight />
        <StatCard label="Active Referrals" value={stats.activeReferrals} icon={Users} />
        <StatCard label="Est. Revenue" value={stats.totalRevenue} icon={DollarSign} format="currency" />
        <StatCard label="Conversion Rate" value={conversionRate} icon={ArrowRight} format="percent" highlight />
      </div>

      {/* Conversion Funnel */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4">
        <p className="text-sm font-bold text-white">Conversion Funnel</p>
        <div className="space-y-3">
          <FunnelBar label="Clicks → Sign-ups" value={stats.signups} max={stats.clicks} color="bg-blue-500" />
          <FunnelBar label="Sign-ups → Paid" value={totalConversions} max={stats.signups} color="bg-primary-500" />
          <FunnelBar label="Free → Basic" value={stats.freeToBasic} max={totalConversions || 1} color="bg-emerald-500" />
          <FunnelBar label="Free → Premium" value={stats.freeToPremium} max={totalConversions || 1} color="bg-amber-500" />
          <FunnelBar label="Trial → Subscriber" value={stats.trialToSubscriber} max={totalConversions || 1} color="bg-violet-500" />
        </div>
      </div>

      {/* How it works */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
        <p className="text-sm font-bold text-white mb-4">How It Works</p>
        <div className="space-y-3">
          {[
            { step: '1', text: 'Share your creator code or referral link with your audience' },
            { step: '2', text: 'Your followers sign up using your code' },
            { step: '3', text: 'When they upgrade to a paid plan, you earn your referral %' },
            { step: '4', text: 'Payouts are processed monthly — contact support for details' },
          ].map(({ step, text }) => (
            <div key={step} className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-primary-500/20 text-primary-400 text-xs font-black flex items-center justify-center shrink-0 mt-0.5">
                {step}
              </span>
              <p className="text-sm text-slate-400">{text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
